import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, Link } from 'react-router-dom';
import { Container, Paper, Typography, TextField, Button, Box, Alert, useTheme, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { LoginCredentials } from '../types/auth';
import CaptchaField from '../components/CaptchaField';
import PasswordField from '../components/PasswordField';
import { isCaptchaSiteKeyConfigured } from '../utils/captcha';

const Login: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { login, error, isAuthenticated, user } = useAuth();
  const { register, handleSubmit, formState: { errors } } = useForm<LoginCredentials>();
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaError, setCaptchaError] = useState('');
  const [captchaResetKey, setCaptchaResetKey] = useState(0);
  const captchaEnabled = useMemo(() => isCaptchaSiteKeyConfigured(), []);

  // Проверяваме дали потребителят вече е автентикиран
  useEffect(() => {
    if (isAuthenticated && user) {
      // Ако е автентикиран, пренасочваме към правилното табло според типа потребител
      const userId = user.id?.toString() || localStorage.getItem('user_id') || '1';
      const userType = user.user_type || localStorage.getItem('user_type') || 'client';
      
      // Проверяваме за запаметен URL за пренасочване
      const savedRedirect = localStorage.getItem('redirectAfterLogin');
      
      // Уверяваме се, че URL-то за пренасочване съдържа ID на потребителя и правилния тип
      let redirectUrl = '';
      
      if (savedRedirect && savedRedirect.includes(`/${userId}/`)) {
        // Проверяваме дали запаметеният URL съответства на типа потребител
        const isProfessionalPath = savedRedirect.includes('/professional/');
        const isClientPath = savedRedirect.includes('/client/');
        
        if ((userType === 'professional' && isProfessionalPath) ||
            (userType === 'client' && isClientPath)) {
          redirectUrl = savedRedirect;
        } else {
          // Ако типът не съвпада, създаваме нов URL
          redirectUrl = `/profile/${userType}/${userId}/dashboard`;
        }
      } else {
        // Създаваме нов URL според типа потребител
        redirectUrl = `/profile/${userType}/${userId}/dashboard`;
      }
      
      localStorage.removeItem('redirectAfterLogin'); // Премахваме redirect URL след използване
      navigate(redirectUrl);
    }
  }, [isAuthenticated, navigate, user]);

  const onSubmit = async (data: LoginCredentials) => {
    try {
      if (captchaEnabled && !captchaToken) {
        setCaptchaError(t('auth.captcha_required'));
        return;
      }
      setCaptchaError('');
      // Използваме login функцията от AuthContext вместо директно API извикване
      await login(data.email, data.password, captchaToken);
      
      // Автентикацията и пренасочването се обработват от AuthContext и useEffect по-горе
    } catch (error: any) {
      console.error('Login error:', error);
      setCaptchaToken('');
      setCaptchaResetKey((current) => current + 1);
      if (captchaEnabled) {
        setCaptchaError(t('auth.captcha_retry'));
      }
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: { xs: 4, sm: 8 }, px: { xs: 2, sm: 3 } }}>
      <Paper 
        elevation={isMobile ? 0 : 3}
        sx={{ 
          p: { xs: 3, sm: 4 },
          borderRadius: { xs: 2, sm: 3 }
        }}
      >
        <Typography 
          variant={isMobile ? 'h5' : 'h4'} 
          component="h1" 
          sx={{ 
            textAlign: 'center', 
            mb: { xs: 3, sm: 4 },
            fontWeight: 600 
          }}
        >
          {t('common.login')}
        </Typography>
        
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 2.5 } }}>
            <TextField
              fullWidth
              label={t('common.email')}
              type="email"
              autoComplete="email"
              error={!!errors.email}
              helperText={errors.email?.message}
              {...register('email', {
                required: t('auth.email_required'),
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: t('auth.invalid_email'),
                },
              })}
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: { xs: '16px', sm: '14px' }, // 16px за mobile предотвратява zoom
                }
              }}
              inputProps={{ 'data-testid': 'login-email-input' }}
            />

            <PasswordField
              fullWidth
              label={t('common.password')}
              autoComplete="current-password"
              error={!!errors.password}
              helperText={errors.password?.message}
              {...register('password', {
                required: t('auth.password_required'),
                minLength: {
                  value: 6,
                  message: t('auth.password_length'),
                },
              })}
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: { xs: '16px', sm: '14px' },
                }
              }}
              inputProps={{ 'data-testid': 'login-password-input' }}
            />

            {error && (
              <Alert severity="error">{error}</Alert>
            )}

            <CaptchaField
              resetKey={captchaResetKey}
              onTokenChange={(token) => {
                setCaptchaToken(token);
                if (token) {
                  setCaptchaError('');
                }
              }}
              errorText={captchaError}
            />

            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              sx={{ 
                mt: 1,
                py: { xs: 1.5, sm: 1.75 },
                fontSize: { xs: '1rem', sm: '1.1rem' }
              }}
              data-testid="login-submit-button"
            >
              {t('auth.sign_in')}
            </Button>

            <Box sx={{ textAlign: 'center', mt: 1 }}>
              <Link 
                to="/forgot-password" 
                style={{ 
                  fontSize: isMobile ? '0.875rem' : '0.9rem',
                  textDecoration: 'none'
                }}
              >
                {t('forgot_password.title')}
              </Link>
            </Box>

            <Box sx={{ textAlign: 'center', mt: 1 }}>
              <Typography variant={isMobile ? 'body2' : 'body1'}>
                {t('auth.no_account')}{' '}
                <Link 
                  to="/register" 
                  style={{ textDecoration: 'none', fontWeight: 500 }}
                >
                  {t('auth.sign_up')}
                </Link>
              </Typography>
            </Box>

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                <Link to="/terms" style={{ color: 'inherit' }}>
                  {t('legal.terms_title')}
                </Link>
                {' · '}
                <Link to="/privacy" style={{ color: 'inherit' }}>
                  {t('legal.privacy_title')}
                </Link>
              </Typography>
            </Box>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

export default Login; 